import React, { useEffect, useMemo, useState } from 'react';
import { loadUncertaintySettings, saveUncertaintySettings } from './uncertaintySettings';
import { ETH } from '../economics/ecoShared';
import { useProductionForecastStore } from '../../hooks/useProductionForecastStore';
import type { DeclineDataPoint } from './declineCalculations';
import { buildSensitivityModelCurves, buildSensitivityModelResults } from './sensitivityModelCalculations';
import type { SensitivityDeclineModel } from './sensitivityModelCalculations';
import { runMonteCarlo, summarizeMonteCarlo } from './uncertaintyCalculations';
import type { MonteCarloResult } from './uncertaintyCalculations';

export default function UncertaintyPanel({ projectId }: { projectId: number }) {
  const project = useProductionForecastStore((state) => state.projects[String(projectId)]);
  
  const initialSettings = useMemo(() => loadUncertaintySettings(projectId), [projectId]);

  const [selectedModel, setSelectedModel] = useState<SensitivityDeclineModel>(initialSettings.selectedModel);
  const [forecastYears, setForecastYears] = useState(initialSettings.forecastYears);
  const [simulations, setSimulations] = useState(initialSettings.simulations);
  const [productivityPct, setProductivityPct] = useState(initialSettings.productivityPct);
  const [declinePct, setDeclinePct] = useState(initialSettings.declinePct);
  const [engineeringPct, setEngineeringPct] = useState(initialSettings.engineeringPct);
  const [terminalDeclinePct, setTerminalDeclinePct] = useState(initialSettings.terminalDeclinePct);
  const [seed, setSeed] = useState(initialSettings.seed);

  useEffect(() => {
    saveUncertaintySettings(projectId, { selectedModel, forecastYears, simulations, productivityPct, declinePct, engineeringPct, terminalDeclinePct, seed });
  }, [projectId, selectedModel, forecastYears, simulations, productivityPct, declinePct, engineeringPct, terminalDeclinePct, seed]);
  
  const declineData = useMemo<DeclineDataPoint[]>(() => {
    const rows = project?.productionRows ?? [];
    const valid = rows.map((row) => ({ date: row.date, q: Number(row.gasRate) })).filter((row) => row.date && Number.isFinite(row.q) && row.q > 0).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    if (valid.length === 0) return [];

    const firstTime = new Date(valid[0].date).getTime();
    return valid.map((row) => ({ date: row.date, q: row.q, t: (new Date(row.date).getTime() - firstTime) / 86400000 }));
  }, [project]);

  const lastT = declineData.length > 0 ? declineData[declineData.length - 1].t : 0;
  const years = clamp(numberOrDefault(forecastYears, 5), 0.1, 50);
  const forecastEndT = lastT + years * 365;
  const declineRange = clamp(numberOrDefault(declinePct, 20), 0, 80) / 100;
  const extendedEndT = lastT + (forecastEndT - lastT) * (1 + declineRange);

  const modelResults = useMemo(() => buildSensitivityModelResults(declineData, lastT, forecastEndT, numberOrDefault(terminalDeclinePct, 5)), [declineData, lastT, forecastEndT, terminalDeclinePct]);
  const extendedCurves = useMemo(() => buildSensitivityModelCurves(declineData, lastT, extendedEndT, numberOrDefault(terminalDeclinePct, 5), 320), [declineData, lastT, extendedEndT, terminalDeclinePct]);

  const selectedResult = useMemo(() => modelResults.find((item) => item.key === selectedModel) ?? modelResults[0] ?? null, [modelResults, selectedModel]);
  const selectedCurve = useMemo(() => extendedCurves.find((item) => item.key === selectedModel) ?? extendedCurves[0] ?? null, [extendedCurves, selectedModel]);

  const monteCarlo = useMemo(() => {
    if (!selectedCurve) return null;

    return runMonteCarlo(selectedCurve, {
      simulations: clamp(numberOrDefault(simulations, 1000), 100, 10000),
      displayEndT: forecastEndT,
      productivityPct: clamp(numberOrDefault(productivityPct, 15), 0, 90),
      declinePct: clamp(numberOrDefault(declinePct, 20), 0, 80),
      engineeringPct: clamp(numberOrDefault(engineeringPct, 10), 0, 90),
      seed,
    });
  }, [selectedCurve, simulations, forecastEndT, productivityPct, declinePct, engineeringPct, seed]);

  const summary = useMemo(() => monteCarlo ? summarizeMonteCarlo(monteCarlo) : null, [monteCarlo]);

  const belowBaselinePct = useMemo(() => {
    if (!monteCarlo || !selectedResult || monteCarlo.cumulativeSamples.length === 0) return null;
    const count = monteCarlo.cumulativeSamples.filter((value) => value < selectedResult.futureCumulative).length;
    return count / monteCarlo.cumulativeSamples.length * 100;
  }, [monteCarlo, selectedResult]);
  
  if (!project) return <div style={{ color: ETH.textMuted }}>正在初始化不确定性分析...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
        <div>
          <h3 style={{ margin: 0 }}>不确定性分析</h3>
          <div style={{ marginTop: 5, fontSize: 11, color: ETH.textMuted }}>基于生产递减模型与参数不确定范围开展 Monte Carlo 预测，形成 P90 / P50 / P10 产能区间</div>
        </div>
        <button type="button" onClick={() => setSeed((value) => value + 1)} style={buttonStyle}>重新模拟</button>
      </div>

      <div style={{ marginTop: 14, padding: 14, background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}` }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Monte Carlo 设置</div>

        <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14 }}>
          <label style={labelStyle}>
            <span style={labelTextStyle}>基准递减模型</span>
            <select value={selectedModel} onChange={(event) => setSelectedModel(event.target.value as SensitivityDeclineModel)} style={inputStyle}>
              {modelResults.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
            </select>
          </label>

          <ParamInput label="预测年限" value={forecastYears} unit="年" onChange={setForecastYears} />
          <ParamInput label="模拟次数" value={simulations} unit="次" onChange={setSimulations} />
          <ParamInput label="产能水平不确定性" value={productivityPct} unit="±%" onChange={setProductivityPct} />
          <ParamInput label="递减速度不确定性" value={declinePct} unit="±%" onChange={setDeclinePct} />
          <ParamInput label="工程参数综合不确定性" value={engineeringPct} unit="±%" onChange={setEngineeringPct} />

          {selectedModel === 'segmented' && <ParamInput label="末期有效年递减率" value={terminalDeclinePct} unit="%" onChange={setTerminalDeclinePct} />}
        </div>

        <div style={{ marginTop: 10, fontSize: 10, color: ETH.textMuted }}>
          当前不确定范围为可调整的工程初始假设，不代表具体区块的统计分布；正式应用时应使用历史井、数值模拟或区块统计进行校准。
        </div>
      </div>

      {declineData.length < 3 || !selectedResult || !monteCarlo ? (
        <div style={{ marginTop: 14, padding: 30, textAlign: 'center', color: ETH.textMuted, background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}` }}>
          至少需要 3 条有效生产历史数据后才能开展不确定性分析。
        </div>
      ) : (
        <>
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
            <ResultCard title="确定性基准" value={formatWanVolume(selectedResult.futureCumulative)} description={`${selectedResult.label}，R² = ${selectedResult.r2.toFixed(4)}`} />
            <ResultCard title="P90 累计产气" value={formatWanVolume(monteCarlo.p90Cumulative)} description="保守估计 / 低值侧" />
            <ResultCard title="P50 累计产气" value={formatWanVolume(monteCarlo.p50Cumulative)} description="中位估计" strong />
            <ResultCard title="P10 累计产气" value={formatWanVolume(monteCarlo.p10Cumulative)} description="乐观估计 / 高值侧" />
          </div>

          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            <ResultCard title="P90 末期日产气" value={formatWanRate(monteCarlo.p90FinalRate)} description="保守情景" />
            <ResultCard title="P50 末期日产气" value={formatWanRate(monteCarlo.p50FinalRate)} description="中位情景" strong />
            <ResultCard title="P10 末期日产气" value={formatWanRate(monteCarlo.p10FinalRate)} description="乐观情景" />
          </div>

          {summary && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Monte Carlo 统计摘要</div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
                <ResultCard title="平均未来累计产气" value={formatWanVolume(summary.mean)} description="全部模拟样本算术平均值" />

                <ResultCard title="标准差" value={formatWanVolume(summary.std)} description="累计产气离散程度" />

                <ResultCard title="变异系数 CV" value={`${summary.cvPct.toFixed(2)}%`} description="标准差 / 平均值" />

                <ResultCard title="不确定性等级" value={summary.uncertaintyLevel} description="依据 P10-P90 相对跨度的程序提示" strong />

                <ResultCard title="P10-P90 相对跨度" value={`${summary.spreadPct.toFixed(2)}%`} description="(P10−P90) / P50" />

                <ResultCard title="P50 下行空间" value={`${summary.downsidePct.toFixed(2)}%`} description="P50 到 P90 的相对下降幅度" />

                <ResultCard title="P50 上行空间" value={`${summary.upsidePct.toFixed(2)}%`} description="P50 到 P10 的相对增加幅度" />

                <ResultCard title="低于确定性基准概率" value={belowBaselinePct !== null ? `${belowBaselinePct.toFixed(1)}%` : '--'} description="Monte Carlo 样本低于当前基准预测的比例" />
              </div>
            </div>
          )}
          
          <PercentileCurveChart history={declineData} result={monteCarlo} />
          <DistributionChart result={monteCarlo} />

          <div style={{ marginTop: 14, padding: 14, background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}`, fontSize: 11, color: ETH.textMuted, lineHeight: 1.8 }}>
            <div>· 本页面采用油气储量/产量预测中常见的概率口径：P90 为较保守的低值估计，P50 为中位估计，P10 为较乐观的高值估计。</div>
            <div>· 这里不是简单把 P10 当作第 10 百分位：程序内部按产量从低到高排序，因此 P90 对应第 10 百分位、P50 对应第 50 百分位、P10 对应第 90 百分位。</div>
            <div>· 当前模拟同时考虑产能水平、递减速度和工程综合因素；三类不确定范围均可调整。</div>
            <div>· 当前采用三角分布，众数为基准值 1.0；后续可继续升级为正态、对数正态和自定义经验分布。</div>
          </div>
        </>
      )}
    </div>
  );
}

function PercentileCurveChart({ history, result }: { history: DeclineDataPoint[]; result: MonteCarloResult }) {
  const width = 920;
  const height = 390;
  const left = 66;
  const right = 30;
  const top = 30;
  const bottom = 55;
  const plotW = width - left - right;
  const plotH = height - top - bottom;

  const allT = [...history.map((p) => p.t), ...result.curve.map((p) => p.t)];
  const allQ = [...history.map((p) => p.q), ...result.curve.flatMap((p) => [p.p90, p.p50, p.p10])];
  const maxT = Math.max(1, ...allT);
  const maxQ = Math.max(1, ...allQ) * 1.08;
  const x = (value: number) => left + value / maxT * plotW;
  const y = (value: number) => top + (1 - value / maxQ) * plotH;

  const makePath = (key: 'p90' | 'p50' | 'p10') => result.curve.map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(point.t)} ${y(point[key])}`).join(' ');

  return (
    <div style={panelStyle}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>P90 / P50 / P10 产量预测曲线</div>
      <div style={{ marginTop: 4, fontSize: 10, color: ETH.textMuted }}>历史生产与 Monte Carlo 概率预测带</div>

      <div style={{ marginTop: 10, display: 'flex', gap: 18, fontSize: 10, color: ETH.textMuted }}>
        <Legend color="#777" text="P90 保守" />
        <Legend color="#b64f35" text="P50 中位" />
        <Legend color="#3c6e91" text="P10 乐观" />
      </div>

      <div style={{ marginTop: 8, overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', minWidth: 760, height }}>
          <rect width={width} height={height} fill={ETH.bg} />

          {Array.from({ length: 6 }, (_, i) => i).map((i) => {
            const q = maxQ * i / 5;
            return <g key={`y-${i}`}><line x1={left} x2={width - right} y1={y(q)} y2={y(q)} stroke={ETH.headerBorder} /><text x={left - 8} y={y(q) + 4} textAnchor="end" fontSize={10} fill={ETH.textMuted}>{q.toFixed(1)}</text></g>;
          })}

          {Array.from({ length: 6 }, (_, i) => i).map((i) => {
            const t = maxT * i / 5;
            return <g key={`x-${i}`}><line x1={x(t)} x2={x(t)} y1={top} y2={height - bottom} stroke={ETH.headerBorder} /><text x={x(t)} y={height - bottom + 18} textAnchor="middle" fontSize={10} fill={ETH.textMuted}>{t.toFixed(0)}</text></g>;
          })}

          <path d={makePath('p90')} fill="none" stroke="#777" strokeWidth={2} />
          <path d={makePath('p50')} fill="none" stroke="#b64f35" strokeWidth={3} />
          <path d={makePath('p10')} fill="none" stroke="#3c6e91" strokeWidth={2} />

          {history.map((point, index) => <circle key={`${point.date}-${index}`} cx={x(point.t)} cy={y(point.q)} r={4} fill={ETH.text} />)}

          <text x={left + plotW / 2} y={height - 10} textAnchor="middle" fontSize={11} fill={ETH.textSec}>生产时间 / d</text>
          <text x={16} y={top + plotH / 2} textAnchor="middle" fontSize={11} fill={ETH.textSec} transform={`rotate(-90 16 ${top + plotH / 2})`}>日产气 / 10⁴m³/d</text>
        </svg>
      </div>
    </div>
  );
}

function DistributionChart({ result }: { result: MonteCarloResult }) {
  const values = result.cumulativeSamples.map((value) => value * 1e4);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const binCount = 20;
  const span = Math.max(1, max - min);
  const bins = Array.from({ length: binCount }, () => 0);

  values.forEach((value) => {
    const index = Math.min(binCount - 1, Math.floor((value - min) / span * binCount));
    bins[index] += 1;
  });

  const width = 920;
  const height = 330;
  const left = 65;
  const right = 30;
  const top = 30;
  const bottom = 55;
  const plotW = width - left - right;
  const plotH = height - top - bottom;
  const maxCount = Math.max(1, ...bins);
  const x = (value: number) => left + (value - min) / span * plotW;
  const y = (value: number) => top + (1 - value / maxCount) * plotH;
  const binWidth = plotW / binCount;

  return (
    <div style={panelStyle}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>未来累计产气概率分布</div>
      <div style={{ marginTop: 4, fontSize: 10, color: ETH.textMuted }}>{result.simulations} 次 Monte Carlo 模拟结果</div>

      <div style={{ marginTop: 10, overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', minWidth: 760, height }}>
          <rect width={width} height={height} fill={ETH.bg} />

          {bins.map((count, index) => <rect key={index} x={left + index * binWidth + 1} y={y(count)} width={Math.max(1, binWidth - 2)} height={height - bottom - y(count)} fill="#999" opacity={0.65} />)}

          <PercentileLine value={result.p90Cumulative * 1e4} label="P90" x={x} top={top} bottom={height - bottom} color="#777" />
          <PercentileLine value={result.p50Cumulative * 1e4} label="P50" x={x} top={top} bottom={height - bottom} color="#b64f35" />
          <PercentileLine value={result.p10Cumulative * 1e4} label="P10" x={x} top={top} bottom={height - bottom} color="#3c6e91" />

          <text x={left + plotW / 2} y={height - 10} textAnchor="middle" fontSize={11} fill={ETH.textSec}>未来累计产气 / m³</text>
          <text x={16} y={top + plotH / 2} textAnchor="middle" fontSize={11} fill={ETH.textSec} transform={`rotate(-90 16 ${top + plotH / 2})`}>模拟次数</text>
        </svg>
      </div>
    </div>
  );
}

function PercentileLine({ value, label, x, top, bottom, color }: { value: number; label: string; x: (value: number) => number; top: number; bottom: number; color: string }) {
  return <g><line x1={x(value)} x2={x(value)} y1={top} y2={bottom} stroke={color} strokeWidth={2} /><text x={x(value)} y={top - 7} textAnchor="middle" fontSize={10} fill={color}>{label}</text></g>;
}

function Legend({ color, text }: { color: string; text: string }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><span style={{ width: 20, height: 3, display: 'inline-block', background: color }} />{text}</div>;
}

function ParamInput({ label, value, unit, onChange }: { label: string; value: string; unit: string; onChange: (value: string) => void }) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="number" value={value} onChange={(event) => onChange(event.target.value)} style={inputStyle} />
        <span style={{ fontSize: 10, color: ETH.textMuted, whiteSpace: 'nowrap' }}>{unit}</span>
      </div>
    </label>
  );
}

function ResultCard({ title, value, description, strong = false }: { title: string; value: string; description: string; strong?: boolean }) {
  return (
    <div style={{ padding: 14, background: ETH.canvasBg, border: strong ? '1px solid #b64f35' : `1px solid ${ETH.headerBorder}` }}>
      <div style={{ fontSize: 10, color: ETH.textMuted }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: strong ? 20 : 17, fontWeight: 800, fontFamily: ETH.fontMono }}>{value}</div>
      <div style={{ marginTop: 5, fontSize: 10, color: ETH.textMuted }}>{description}</div>
    </div>
  );
}

function numberOrDefault(value: string, fallback: number) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}



const panelStyle: React.CSSProperties = { marginTop: 14, padding: 14, background: ETH.canvasBg, border: `1px solid ${ETH.headerBorder}` };
const labelStyle: React.CSSProperties = { display: 'grid', gap: 5 };
const labelTextStyle: React.CSSProperties = { fontSize: 11, color: ETH.textSec };
const inputStyle: React.CSSProperties = { width: '100%', padding: '6px 8px', boxSizing: 'border-box', border: `1px solid ${ETH.headerBorder}`, background: ETH.bg, color: ETH.text, outline: 'none' };
const buttonStyle: React.CSSProperties = { padding: '7px 12px', border: `1px solid ${ETH.headerBorder}`, background: ETH.bg, color: ETH.textSec, cursor: 'pointer' };
function formatWanRate(value: number, digits = 3) { return Number.isFinite(value) ? `${value.toFixed(digits)} ×10⁴m³/d` : '--'; }

function formatWanVolume(value: number, digits = 2) { return Number.isFinite(value) ? `${value.toFixed(digits)} ×10⁴m³` : '--'; }